import { Component, Input, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LsqApiServiceListSource } from '@lsq-frontend/angular-shared/api-service';
import {
    FastTrackRouteQueryParams,
    FeatureFlags
} from '@lsq-frontend/fasttrack-shared/app-constants';
import {
    ACCOUNT_SUPPLIER_DETAILS,
    atlas,
    BUYER_ACCOUNT_INVOICE_DETAIL,
    BUYER_SUPPLIER_DETAILS,
    SUPPLY_CHAIN_DIRECT_ACCOUNT_INVOICES,
    SUPPLY_CHAIN_DIRECT_ACCOUNT_OVERVIEW,
    SUPPLY_CHAIN_DIRECT_BUYER_INVOICES,
    SUPPLY_CHAIN_DIRECT_BUYER_OVERVIEW
} from '@lsq-frontend/fasttrack-shared/routing-atlas';
import { InvoiceSummary } from '@lsq-frontend/remote-services/all-java/financing-mk0-services';
import {
    Color,
    getDynamicRouterLink,
    LifecycleAware,
    mapToKey,
    TableAppearance
} from '@lsq-frontend/common-ui';
import {
    ClaimService,
    FeatureFlagService,
    getRouterLinkById,
    getRouterLinkUrlById,
    Icons,
    ResponsiveService,
    WindowReferenceService
} from '@lsq-frontend/common-ui';
import { I18n } from '@ngx-translate/i18n-polyfill';
import { map } from 'rxjs/operators';

import { DashboardClaim } from '@lsq-frontend/remote-services/enterprise/common';
import {
    ListInvoiceSummaryForBuyerRequest2,
    ListInvoiceSummaryForBuyerResponse
} from '@lsq-frontend/remote-services/financing-graphql';
import { Observable } from 'rxjs';

@LifecycleAware()
@Component({
    selector: 'buyer-invoices-payable-table',
    template: `
        <card
            [headerTitle]="!(payLater$ | async) ? invoicesTitleI18n : topAgingInvoicesTitleI18n"
            id="invoices-payable-table-card"
            class="card table-card"
            [hasFooter]="!(listInvoicesDataSource.loading$ | async)"
        >
            <div
                card-body
                class="card-body"
                [class.loading]="listInvoicesDataSource.loading$ | async"
            >
                <lsq-data-table
                    [dataSource]="listInvoicesDataSource"
                    [isPaginated]="false"
                    [appearance]="tableAppearance.Default"
                >
                    <lsq-column
                        [column]="{
                            header: supplierInvoiceListHeaderI18n,
                            key: 'sellerName',
                            sortable: false,
                            className: 'first-column',
                            width: '25%'
                        }"
                    >
                        <ng-template [custom-cell] let-row="row">
                            <a
                                class="p not-bold supplier-name"
                                (click)="goSupplierDetails(row.sellerKey)"
                                queryParamsHandling="preserve"
                            >
                                <lsq-truncate
                                    [value]="row.sellerName"
                                    [maxLength]="25"
                                ></lsq-truncate>
                            </a>
                        </ng-template>
                    </lsq-column>

                    <lsq-column
                        [column]="{
                            header: invoiceNumberInvoiceListHeaderI18n,
                            key: 'invoiceNumber',
                            truncateAfter: invoiceNumberTruncateAfter$ | async | notNull,
                            reverseTruncate: true,
                            sortable: false,
                            width: '25%'
                        }"
                    >
                        <ng-template [custom-cell] let-row="row">
                            <a
                                *ngIf="buyerAccountInvoiceDetails$ | async"
                                class="not-bold"
                                (click)="openInvoiceDetailsBound(row)"
                            >
                                <lsq-truncate
                                    [value]="row.invoiceNumber"
                                    [maxLength]="15"
                                ></lsq-truncate
                            ></a>
                            <lsq-truncate
                                *ngIf="!(buyerAccountInvoiceDetails$ | async)"
                                [value]="row.invoiceNumber"
                                [maxLength]="15"
                            ></lsq-truncate>
                        </ng-template>
                    </lsq-column>
                    <lsq-currency-column
                        [column]="{
                            header: invoiceAmountInvoiceListHeaderI18n,
                            key: 'finalApprovedAmount',
                            sortable: false,
                            width: '25%'
                        }"
                    >
                    </lsq-currency-column>
                    <lsq-column
                        [column]="{
                            header: typeInvoiceListHeaderI18n,
                            key: 'type',
                            sortable: false,
                            width: '25%'
                        }"
                    >
                    </lsq-column>
                </lsq-data-table>
            </div>
            <div card-footer class="card-footer-table">
                <a
                    href="javascript:void(0)"
                    id="buyer-invoices-payable-card-view-all"
                    size="md"
                    (click)="navigateToInvoices()"
                >
                    {{ allInvoicesI18n }}
                    ({{ totalCountBuyerInvoices$ | async | number }})
                </a>
            </div>
        </card>
    `
})
export class BuyerInvoicesPayableTableComponent implements OnInit {
    @Input() public listInvoicesDataSource!: LsqApiServiceListSource<
        ListInvoiceSummaryForBuyerResponse,
        ListInvoiceSummaryForBuyerRequest2
    >;
    public readonly icons = Icons;
    public readonly color = Color;
    public readonly tableAppearance = TableAppearance;
    private _isFinancingAccountPortalUser = false;

    public buyerAccountInvoiceDetails$ = this._featureFlagService.isOn(
        FeatureFlags.BuyerAccountInvoiceDetails
    );
    public payLater$ = this._featureFlagService.isOn(FeatureFlags.PayLater);
    public invoicesTitleI18n = this._i18n({
        value: 'Invoices Payable to LSQ',
        meaning: `Data describing which invoices are due to LSQ`,
        description: 'Section title'
    });
    public topAgingInvoicesTitleI18n = this._i18n({
        value: 'Top Invoices Payable',
        meaning: `Data describing which invoices`,
        description: 'Section title'
    });
    public supplierInvoiceListHeaderI18n = this._i18n({
        value: 'Supplier',
        description: `Supplier Name header`
    });
    public invoiceNumberInvoiceListHeaderI18n = this._i18n({
        value: 'Invoice Number',
        description: `Invoice # header`
    });
    public typeInvoiceListHeaderI18n = this._i18n({
        value: 'Type',
        description: `Type of the Invoice (FlexTerm and Original terms)`
    });
    public invoiceAmountInvoiceListHeaderI18n = this._i18n({
        value: 'Invoice Amount',
        description: `Invoice Amount header`
    });
    public noInvoicesHeaderI18n = this._i18n({
        value: 'You currently have no invoices to show.',
        description: 'Header for when when user has no invoices'
    });
    public allInvoicesI18n = this._i18n({
        value: 'All Invoices',
        meaning: `Click to view all your (the buyer's) invoices in more detail`,
        description: 'Call-to-action button'
    });
    public readonly invoiceNumberTruncateAfter$ = this.windowReferenceService.resizeStream.pipe(
        map((dimensions) => {
            if (dimensions.width > 1525) {
                return 29;
            }

            if (dimensions.width > 1375) {
                return 18;
            }

            if (dimensions.width > 1199) {
                return 14;
            }
            return 29;
        })
    );

    public totalCountBuyerInvoices$: Observable<number>;

    constructor(
        public responsiveService: ResponsiveService,
        private _router: Router,
        private _i18n: I18n,
        public windowReferenceService: WindowReferenceService,
        private _featureFlagService: FeatureFlagService,
        private _claimService: ClaimService
    ) {
        this._isFinancingAccountPortalUser = this._claimService.isOnSync(
            DashboardClaim.FinancingAccountPortal
        );
    }

    public ngOnInit(): void {
        this.totalCountBuyerInvoices$ = this.listInvoicesDataSource.data$.pipe(
            mapToKey('totalCountBuyerInvoices')
        );
    }

    public openInvoiceDetailsBound(invoice: InvoiceSummary): void {
        this._router.navigateByUrl(
            this._router.createUrlTree(
                getDynamicRouterLink(
                    getRouterLinkById(atlas, BUYER_ACCOUNT_INVOICE_DETAIL, {
                        noStandaloneLeadingSlash: true
                    }),
                    { invoiceKey: invoice.refInvoiceKey }
                ),
                {
                    queryParams: {
                        [FastTrackRouteQueryParams.PreviousRouteId]:
                            SUPPLY_CHAIN_DIRECT_ACCOUNT_OVERVIEW
                    }
                }
            )
        );
    }

    public async navigateToInvoices(): Promise<void> {
        await this._router.navigateByUrl(
            getRouterLinkUrlById(
                atlas,
                this._isFinancingAccountPortalUser
                    ? SUPPLY_CHAIN_DIRECT_ACCOUNT_INVOICES
                    : SUPPLY_CHAIN_DIRECT_BUYER_INVOICES
            )
        );
    }

    public goSupplierDetails(sellerKey: string) {
        this._router.navigateByUrl(
            this._router.createUrlTree(
                getDynamicRouterLink(
                    getRouterLinkById(
                        atlas,
                        this._isFinancingAccountPortalUser
                            ? ACCOUNT_SUPPLIER_DETAILS
                            : BUYER_SUPPLIER_DETAILS,
                        {
                            noStandaloneLeadingSlash: true
                        }
                    ),
                    { sellerKey }
                ),
                {
                    queryParams: {
                        [FastTrackRouteQueryParams.PreviousRouteId]: this
                            ._isFinancingAccountPortalUser
                            ? SUPPLY_CHAIN_DIRECT_ACCOUNT_OVERVIEW
                            : SUPPLY_CHAIN_DIRECT_BUYER_OVERVIEW
                    }
                }
            )
        );
    }
}
